/**
 * The meeting itself: a stage the size of the window, with the controls
 * pinned under it.
 *
 * It is not the shared dialog. A dialog is as tall as what is in it, and what
 * is in a meeting changes all the time — someone joins, a notice appears, a
 * screen starts — so everything under the thumb moved each time. Here the
 * frame is fixed: the cards are fitted into the stage, notices lie over it,
 * and Leave call is where it was a second ago.
 *
 * See _design/sketches/Meetings.md §4 and §6–9.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { KeyboardEvent, ReactElement, ReactNode } from 'react'
import { createPortal } from 'react-dom'

import type { Call, UUID } from '@/api/types'
import { useCallConnectionHelp } from '@/hooks/useCallConnectionHelp'
import { joinedPeerIds } from '@/hooks/callPeers'
import type { PeerState } from '@/hooks/callPeers'
import type { CallSessionApi } from '@/components/CallWindow'
import { Button } from '@/components/ui'
import { CallPeople, CallStageArea, participantName, stageTiles, surfaceNoun } from '@/components/CallStageView'
import { announcements, joinOrder, peerPhase, type StageRoster } from '@/lib/callStage'
import styles from './CallWindow.module.css'

type IconName = 'mic' | 'micOff' | 'camera' | 'cameraOff' | 'screen' | 'people' | 'chat' | 'leave' | 'minimise' | 'window'

const ICONS: Record<IconName, ReactNode> = {
  mic: (
    <>
      <rect x="7.6" y="2.8" width="4.8" height="9" rx="2.4" />
      <path d="M4.8 9.4a5.2 5.2 0 0 0 10.4 0M10 14.6V17" />
    </>
  ),
  micOff: (
    <>
      <rect x="7.6" y="2.8" width="4.8" height="9" rx="2.4" />
      <path d="M4.8 9.4a5.2 5.2 0 0 0 10.4 0M10 14.6V17M3.6 3.4 16.4 16.6" />
    </>
  ),
  camera: (
    <>
      <rect x="2.6" y="5.4" width="10.4" height="9.2" rx="2" />
      <path d="m13 9 4.4-2.6v7.2L13 11" />
    </>
  ),
  cameraOff: (
    <>
      <rect x="2.6" y="5.4" width="10.4" height="9.2" rx="2" />
      <path d="m13 9 4.4-2.6v7.2L13 11M3.4 3.4l13.2 13.2" />
    </>
  ),
  screen: (
    <>
      <rect x="2.6" y="4.4" width="14.8" height="9.8" rx="2" />
      <path d="M7.4 17h5.2" />
    </>
  ),
  people: (
    <>
      <circle cx="7.4" cy="7.2" r="2.8" />
      <path d="M2.6 16c.4-2.8 2.2-4.2 4.8-4.2s4.4 1.4 4.8 4.2M13.4 4.8a2.6 2.6 0 0 1 0 5M14.6 12c1.6.5 2.6 1.8 2.8 4" />
    </>
  ),
  chat: <path d="M3.2 4.8h13.6v8.8H9.4L5.8 16.8v-3.2H3.2z" />,
  leave: <path d="M3.4 12.2c3.6-3.4 9.6-3.4 13.2 0l.6-2.2c-4.2-4-10.2-4-14.4 0z" />,
  window: <><rect x="2" y="3" width="16" height="14" rx="2" /><path d="M2 7h16M11 10h4v4M15 10l-5 5" /></>,
  minimise: <path d="M5 13.4 10 8.4l5 5" transform="rotate(180 10 10.9)" />,
}

function Icon({ name }: { name: IconName }): ReactElement {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {ICONS[name]}
    </svg>
  )
}

/**
 * One control: an icon and its word, in a box that does not change size when
 * the word does. `Mute` turning into `Unmute` must not move `Leave call`.
 */
function StageControl({
  icon,
  label,
  onClick,
  off = false,
  pressed,
  tone,
  dimmed = false,
}: {
  icon: IconName
  label: string
  onClick: () => void
  /** The thing this controls is switched off: the icon says so too. */
  off?: boolean
  pressed?: boolean
  tone?: 'leave' | 'action'
  dimmed?: boolean
}): ReactElement {
  return (
    <button
      type="button"
      className={styles.control}
      data-off={off}
      data-tone={tone}
      data-dimmed={dimmed}
      aria-pressed={pressed}
      onClick={onClick}
    >
      <span className={styles.controlIcon}>
        <Icon name={icon} />
      </span>
      <span className={styles.controlLabel}>{label}</span>
    </button>
  )
}

/** How long the call has been up, from the server's own clock. */
function useElapsed(startedAt: string | null): string {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  if (startedAt === null) return ''
  const seconds = Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000))
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const pad = (value: number): string => String(value).padStart(2, '0')
  return hours > 0 ? `${hours}:${pad(minutes % 60)}:${pad(seconds % 60)}` : `${minutes}:${pad(seconds % 60)}`
}

/**
 * What each tile says about its connection, by the clock: `Reconnecting…`
 * after two seconds without a peer, `Could not connect` after twenty (§6).
 * The browser's own state flickers through `disconnected` on any blip.
 */
function usePeerPhases(states: Record<UUID, RTCPeerConnectionState>): Record<UUID, PeerState> {
  const sinceRef = useRef<Record<UUID, number>>({})
  const [now, setNow] = useState(() => Date.now())

  const since = sinceRef.current
  for (const id of Object.keys(since)) {
    if (states[id] === undefined || states[id] === 'connected') delete since[id]
  }
  for (const [id, state] of Object.entries(states)) {
    if (state !== 'connected' && since[id] === undefined) since[id] = Date.now()
  }

  const waiting = Object.values(states).some((state) => state !== 'connected')
  useEffect(() => {
    if (!waiting) return undefined
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [waiting])

  const phases: Record<UUID, PeerState> = {}
  for (const [id, state] of Object.entries(states)) {
    phases[id] = peerPhase(state, Math.max(0, now - (since[id] ?? now)))
  }
  return phases
}

function describeStage(phases: Record<UUID, PeerState>, channelJoined: boolean): string {
  const states = Object.values(phases)
  if (!channelJoined) return 'Connecting…'
  if (states.length === 0) return 'Waiting for the others to join'
  if (states.every((state) => state === 'connected')) return 'Connected'
  if (states.some((state) => state === 'failed')) return 'Could not reach everyone'
  if (states.some((state) => state === 'reconnecting')) return 'Reconnecting…'
  return 'Connecting…'
}

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

export interface InCallStageProps {
  title: string
  call: Call
  selfUserId?: UUID
  session: CallSessionApi
  onHangUp: () => void
  /** Put the meeting away without leaving it. */
  onMinimise?: () => void
  onPopOut?: () => void
  onDock?: () => void
  portalTarget?: HTMLElement
  /** Go to the room's own timeline; the meeting carries on under the pill. */
  onChat?: () => void
  screenSharingEnabled: boolean
  /** "Share a screen as I join" was ticked: offer it, one press away. */
  sharePrompt?: boolean
  onSharePromptDone?: () => void
}

export function InCallStage({
  title,
  call,
  selfUserId,
  session,
  onHangUp,
  onMinimise,
  onPopOut,
  onDock,
  portalTarget,
  onChat,
  screenSharingEnabled,
  sharePrompt = false,
  onSharePromptDone,
}: InCallStageProps): ReactElement {
  const [showPeople, setShowPeople] = useState(false)
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const ownerDocument = portalTarget?.ownerDocument ?? document
  const elapsed = useElapsed(call.started_at)
  const phases = usePeerPhases(session.peerStates)
  const waitingPeers = joinedPeerIds(call, selfUserId).filter(id => session.peerStates[id] !== 'connected')
  const connectionHint = useCallConnectionHelp(session.connected, waitingPeers.length)

  // The order people have stood in so far, for this meeting.
  const orderRef = useRef<{ callId: UUID; order: UUID[] }>({ callId: call.id, order: [] })
  if (orderRef.current.callId !== call.id) orderRef.current = { callId: call.id, order: [] }
  const tiles = stageTiles(call, session, selfUserId, orderRef.current.order, phases)
  orderRef.current.order = joinOrder(
    orderRef.current.order,
    tiles.filter((tile) => !tile.self).map((tile) => tile.id),
  )

  const status = describeStage(phases, session.connected)
  const sharing = session.share.state
  const sharingSelf = sharing !== null && sharing.userId === selfUserId
  const surfaceStream = sharingSelf ? session.share.local : session.share.remote
  const presenting = sharing !== null && surfaceStream !== null
  const presenter = sharing === null ? 'Someone' : participantName(call, sharing.userId)
  const canShare = screenSharingEnabled && session.share.supported
  const videoOffered = call.media === 'video'

  // The stage takes the page, so it takes the focus and gives it back.
  useEffect(() => {
    const restoreTo = ownerDocument.activeElement as HTMLElement | null
    dialogRef.current?.focus()
    const previousOverflow = ownerDocument.body.style.overflow
    ownerDocument.body.style.overflow = 'hidden'
    return () => {
      ownerDocument.body.style.overflow = previousOverflow
      restoreTo?.focus()
    }
  }, [ownerDocument])

  // Joining, leaving and a started or stopped share, said once with the name.
  const roster = useMemo(
    (): StageRoster => ({
      people: tiles.filter((tile) => !tile.self).map((tile) => tile.id),
      sharing: sharing?.userId ?? null,
    }),
    // The ids and the sharer, not the array's identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tiles.map((tile) => tile.id).join(' '), sharing?.userId],
  )
  const rosterRef = useRef<StageRoster | null>(null)
  const [spoken, setSpoken] = useState('')
  useEffect(() => {
    const before = rosterRef.current
    rosterRef.current = roster
    if (before === null) return
    const nameOf = (id: string): string => (id === selfUserId ? 'You' : participantName(call, id))
    const lines = announcements(before, roster, nameOf)
    if (lines.length > 0) setSpoken(lines.join('. '))
    // `call` only names people; the roster is what changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roster])

  const { micEnabled, cameraEnabled, setMicEnabled, setCameraEnabled } = session
  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>): void => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        if (showPeople) setShowPeople(false)
        else onMinimise?.()
        return
      }

      if (event.key === 'Tab') {
        const dialog = dialogRef.current
        if (dialog === null) return
        const items = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE))
        const first = items[0]
        const last = items[items.length - 1]
        if (first === undefined || last === undefined) return
        const active = ownerDocument.activeElement
        if (event.shiftKey && (active === first || active === dialog)) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && active === last) {
          event.preventDefault()
          first.focus()
        }
        return
      }

      // `m` and `v` while the stage has focus, and never while typing.
      if (event.metaKey || event.ctrlKey || event.altKey) return
      const target = event.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return
      if (event.key === 'm') setMicEnabled(!micEnabled)
      else if (event.key === 'v' && videoOffered) setCameraEnabled(!cameraEnabled)
    },
    [ownerDocument, cameraEnabled, micEnabled, onMinimise, setCameraEnabled, setMicEnabled, showPeople, videoOffered],
  )

  return createPortal(
    <div
      ref={dialogRef}
      className={styles.stageDialog}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      tabIndex={-1}
      onKeyDown={onKeyDown}
    >
      <header className={styles.stageHead}>
        <div className={styles.stageHeading}>
          <h2 className={styles.stageTitle}>{title}</h2>
          <span className={styles.status}>
            <span
              className={styles.statusDot}
              data-state={status === 'Connected' ? undefined : status.startsWith('Waiting') ? 'waiting' : 'trouble'}
            />
            {status}
            {elapsed === '' ? '' : ` · ${elapsed}`}
          </span>
        </div>
        {onPopOut === undefined ? null : (
          <button type="button" className={styles.stageWindow} aria-label="Open in a window" title="Move the meeting to its own window" onClick={onPopOut}>
            <Icon name="window" /><span>Open in a window</span>
          </button>
        )}
        {onDock === undefined ? null : (
          <button type="button" className={styles.stageWindow} aria-label="Return to main window" title="Keep the meeting going in the main window" onClick={onDock}>
            <Icon name="window" /><span>Return to main window</span>
          </button>
        )}
        {onMinimise === undefined ? null : (
          <button type="button" className={styles.stageMinimise} aria-label="Minimise" onClick={onMinimise}>
            <Icon name="minimise" />
          </button>
        )}
      </header>

      <CallStageArea
        tiles={tiles}
        surface={
          presenting
            ? {
                stream: surfaceStream,
                presenter,
                source: sharing?.source,
                own: sharingSelf,
                onStop: () => void session.stopShare(),
              }
            : null
        }
        onRetry={session.retryPeer}
      >
        {/* Everything the meeting has to say for itself lies over the stage.
            In the flow it pushed the cards, and the controls, down the page. */}
        <div className={styles.notices}>
          {connectionHint === null ? null : (
            <div className={styles.notice} role="status">
              <span className={styles.noticeText}>{connectionHint}</span>
              {!session.connected || waitingPeers.length === 0 ? null : (
                <Button size="sm" variant="ghost" onClick={() => waitingPeers.forEach(session.retryPeer)}>
                  Reconnect
                </Button>
              )}
            </div>
          )}
          {/* Someone else holds the surface and we asked for it. The takeover
              is a question, never a race. */}
          {session.share.conflict ? (
            <div className={styles.notice} role="alert">
              <span className={styles.noticeText}>
                {sharing === null ? 'Someone else' : presenter} is sharing a {surfaceNoun(sharing?.source)}. Share
                now and you take the stage from them.
              </span>
              <Button size="sm" variant="ghost" onClick={session.dismissShareError}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  session.dismissShareError()
                  void session.startShare({ takeover: true })
                }}
              >
                Share instead
              </Button>
            </div>
          ) : null}

          {session.share.error !== null && !session.share.conflict ? (
            <div className={styles.notice} role="status">
              <span className={styles.noticeText}>{session.share.error}</span>
              <Button size="sm" variant="ghost" onClick={session.dismissShareError}>
                Dismiss
              </Button>
            </div>
          ) : null}

          {/* Asked for in the pre-join. The browser only opens its picker for
              a press, so this is the press. */}
          {sharePrompt && canShare && sharing === null && session.connected ? (
            <div className={styles.notice} role="status">
              <span className={styles.noticeText}>You asked to share a screen as you joined.</span>
              <Button size="sm" variant="ghost" onClick={onSharePromptDone}>
                Not now
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={() => {
                  onSharePromptDone?.()
                  void session.startShare()
                }}
              >
                Choose what to share
              </Button>
            </div>
          ) : null}

          {session.deviceNotice === null || session.deviceNotice === undefined ? null : (
            <div className={styles.notice} role="alert">
              <span className={styles.noticeText}>{session.deviceNotice}</span>
              <Button size="sm" variant="ghost" onClick={session.retryDevices}>
                Try again
              </Button>
            </div>
          )}

          {session.error === null ? null : (
            <div className={styles.notice} role="alert">
              <span className={styles.noticeText}>{session.error}</span>
            </div>
          )}
        </div>

        {/* Audio is the meeting; video is decoration. When the line narrows,
            the camera goes and the client says so, over the controls. */}
        {session.videoPausedForBandwidth ? (
          <div className={styles.strip} role="status">
            <span className={styles.noticeText}>The line is narrow. Your camera is off so the audio holds.</span>
            <Button size="sm" variant="ghost" onClick={() => session.setCameraEnabled(true)}>
              Turn on
            </Button>
          </div>
        ) : null}

        {showPeople ? <CallPeople tiles={tiles} total={tiles.length} onClose={() => setShowPeople(false)} /> : null}
      </CallStageArea>

      <footer className={styles.controls}>
        <StageControl
          icon={session.micEnabled ? 'mic' : 'micOff'}
          label={session.micEnabled ? 'Mute' : 'Unmute'}
          off={!session.micEnabled}
          onClick={() => session.setMicEnabled(!session.micEnabled)}
        />
        {videoOffered ? (
          <StageControl
            icon={session.cameraEnabled ? 'camera' : 'cameraOff'}
            label={session.cameraEnabled ? 'Stop video' : 'Start video'}
            off={!session.cameraEnabled}
            onClick={() => session.setCameraEnabled(!session.cameraEnabled)}
          />
        ) : null}
        {/* While the takeover question is on screen it is the only place to
            answer it: two buttons with the same name, one of them the
            question and one of them the thing that asked it, is a trap. */}
        {canShare && !session.share.conflict ? (
          <StageControl
            icon="screen"
            label={sharingSelf ? 'Stop sharing' : sharing === null ? 'Share screen' : 'Share instead'}
            tone={sharingSelf ? 'action' : undefined}
            onClick={() => {
              if (sharingSelf) void session.stopShare()
              else void session.startShare()
            }}
          />
        ) : null}
        <StageControl
          icon="people"
          label={`People · ${tiles.length}`}
          pressed={showPeople}
          onClick={() => setShowPeople((open) => !open)}
        />
        {onChat === undefined ? null : <StageControl icon="chat" label="Chat" onClick={onChat} />}
        {/* Last in the focus order, and never where the focus starts. */}
        <StageControl icon="leave" label="Leave call" tone="leave" onClick={onHangUp} />
      </footer>

      <div className={styles.srOnly} aria-live="polite">
        {spoken}
      </div>
    </div>,
    portalTarget ?? document.body,
  )
}
